/**
 * Parsea importes en formato español (1.234,56 €, etc.) a número.
 */
export function parseEuroValor(valor) {
  if (valor == null) return 0;
  const limpio = String(valor).replace(/[^0-9,.\-]/g, '').trim();
  if (!limpio) return 0;
  if (limpio.includes(',')) {
    return parseFloat(limpio.replace(/\./g, '').replace(',', '.')) || 0;
  }
  const partes = limpio.split('.');
  if (partes.length === 2 && partes[1].length === 3) {
    return parseFloat(limpio.replace(/\./g, '')) || 0;
  }
  if (partes.length > 2) {
    return parseFloat(limpio.replace(/\./g, '')) || 0;
  }
  return parseFloat(limpio) || 0;
}

/** Fila "Máximo histórico" del rango Pasado (segunda fila tras "Hoy"). */
export function getMaxHistoricoFila(pasado) {
  return Array.isArray(pasado) && pasado.length > 1 ? pasado[1] : null;
}

/** true si el importe total de hoy supera el máximo histórico registrado en la hoja. */
export function superaMaximoHistorico(hoyFila, maxHistoricoFila) {
  if (!hoyFila || !maxHistoricoFila) return false;
  const hoy = parseEuroValor(hoyFila[2]);
  const max = parseEuroValor(maxHistoricoFila[2]);
  return hoy > 0 && max > 0 && hoy > max;
}

/**
 * % por encima (+) o por debajo (−) del máximo histórico:
 * ((importeHoy − máximo) / máximo) × 100.
 * Devuelve null si no hay datos suficientes.
 */
export function pctVsMaximoHistorico(hoyFila, maxHistoricoFila) {
  if (!hoyFila || !maxHistoricoFila) return null;
  const hoy = parseEuroValor(hoyFila[2]);
  const max = parseEuroValor(maxHistoricoFila[2]);
  if (!(hoy > 0) || !(max > 0)) return null;
  return ((hoy - max) / max) * 100;
}

/** Formatea el % con signo y coma decimal (ej. "-0,23 %" / "+1,05 %"). */
export function formatPctVsMaximo(pct, decimales = 2) {
  if (pct == null || Number.isNaN(pct)) return '';
  const signo = pct > 0 ? '+' : '';
  const num = pct.toFixed(decimales).replace('.', ',');
  return `${signo}${num} %`;
}

/** Parsea una fecha DD/MM/YYYY a Date local (medianoche). */
export function parseFechaEs(fechaStr) {
  if (!fechaStr) return null;
  const m = String(fechaStr).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const dia = Number(m[1]);
  const mes = Number(m[2]) - 1;
  const ano = Number(m[3]);
  const fecha = new Date(ano, mes, dia);
  if (fecha.getFullYear() !== ano || fecha.getMonth() !== mes || fecha.getDate() !== dia) {
    return null;
  }
  return fecha;
}

/** Días civiles hasta la fecha (negativo si ya pasó). */
export function diasHastaFecha(fechaStr, ahora = new Date()) {
  const fecha = parseFechaEs(fechaStr);
  if (!fecha) return null;
  const hoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
  const destino = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
  return Math.round((destino.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
}

export function formatDiasRestantes(dias) {
  if (dias == null || Number.isNaN(dias)) return '';
  const n = Math.trunc(dias);
  if (n === 0) return 'Hoy';
  if (n === 1) return '1 día';
  if (n === -1) return 'Hace 1 día';
  if (n > 1) return `${n} días`;
  return `Hace ${Math.abs(n)} días`;
}

/**
 * % de la meta ya cubierto: (objetivo − restante) / objetivo × 100.
 * Requiere importe objetivo y cantidad que falta.
 */
export function porcentajeMetaConseguida(importe, faltan) {
  const objetivo = parseEuroValor(importe);
  if (!(objetivo > 0)) return null;
  const restanteStr = faltan == null ? '' : String(faltan).trim();
  if (!restanteStr) return null;
  const restante = parseEuroValor(faltan);
  return ((objetivo - restante) / objetivo) * 100;
}

export function formatPorcentajeMeta(pct, decimales = 1) {
  if (pct == null || Number.isNaN(pct)) return '';
  return `${pct.toFixed(decimales).replace('.', ',')} %`;
}

/** Extrae metas FIRE del bloque "otros" e incluye días restantes y % conseguido. */
export function parseMetasFire(otros, ahora = new Date()) {
  const metas = [];
  let lastEtiqueta = '';
  for (const fila of otros || []) {
    const importe = String(fila[1] || '').trim();
    if (!importe.match(/[\d.,]+\s*€/)) continue;

    const fechaIdx = fila.findIndex(
      (c, i) => i > 1 && c && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(String(c).trim())
    );
    const fechaAlcance = fechaIdx >= 0 ? String(fila[fechaIdx]).trim() : '';
    const faltanRaw = fechaIdx >= 0 ? String(fila[fechaIdx + 2] || '').trim() : '';
    const faltan = faltanRaw.match(/[\d.,]+/) ? faltanRaw : '';

    const etiqueta = String(fila[0] || '').trim() || lastEtiqueta;
    if (fila[0] && String(fila[0]).trim()) lastEtiqueta = String(fila[0]).trim();

    const diasRestantes = diasHastaFecha(fechaAlcance, ahora);
    const pctConseguido = porcentajeMetaConseguida(importe, faltan);
    const pctBarWidth = pctConseguido == null
      ? 0
      : Math.min(100, Math.max(0, pctConseguido));

    metas.push({
      etiqueta,
      importe,
      descripcion: String(fila[2] || '').trim(),
      faltan,
      fechaAlcance,
      diasRestantes,
      diasRestantesTexto: formatDiasRestantes(diasRestantes),
      pctConseguido,
      pctConseguidoTexto: formatPorcentajeMeta(pctConseguido),
      pctBarWidth
    });
  }
  return metas;
}
